import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { ProductsQueriesUser } from '../../../../requests/queries/ProductsQueries';
import { CategoriesQueriesUser } from '../../../../requests/queries/CategoriesQueries';
import { PricesQueriesClient } from '../../../../requests/queries/PricesQueries';
import { VariantsProductsQueriesUser } from '../../../../requests/queries/VariantsProductsQueries';
import ImagesQueries from '../../../../requests/queries/ImagesQueries';
import {Gallery} from 'pluracore-comps'
import 'pluracore-comps/dist/index.esm.css'

const ProductsManagement = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [products, setProducts] = useState([]);
    const [error, setError] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [showAddCategoryForm, setShowAddCategoryForm] = useState(false);
    const [categories, setCategories] = useState([]);

    const handleCloseModal = () => {
        setSelectedProduct(null);
        fetchProducts();
    }

    const handleSelectProduct = (product) => {
        setSelectedProduct(product);
    }

    const handleDeleteProduct = async (productId) => {
        try {
            setIsLoading(true);
            const res = await ProductsQueriesUser.delete(productId);
            if(res) {
                fetchProducts();
                setError(null);
            } else {
                setError(res.message);
            }
            setIsLoading(false);
        } catch (error) {
            setError(error.response.data.message);
            setIsLoading(false);
        }
    }

    const handleAddCategoryToProduct = async (productId, categoryId) => {
      try {
        setIsLoading(true);
        const res = await CategoriesQueriesUser.addCategoryToProduct(productId, categoryId);
        if(res) {
          fetchProducts();
          setError(null);
        } else {
          setError(res.message);
        }
        setIsLoading(false);
      } catch (error) {
        setError(error.response.data.message);
        setIsLoading(false);
      }
    }

    const handleRemoveCategoryFromProduct = async (productId, categoryId) => {
      try {
        setIsLoading(true);
        const res = await CategoriesQueriesUser.removeCategoryFromProduct(productId, categoryId);
        if(res) {
          fetchProducts();
          setError(null);
        } else {
          setError(res.message);
        }
        setIsLoading(false);
      }
      catch (error) {
        setError(error.response.data.message);
        setIsLoading(false);
      }
    }
    
    const handleCreateProduct = async (e) => {
        e.preventDefault();
        try {
            setIsLoading(true);
            const res = await ProductsQueriesUser.create({name: e.target.name.value});
            if(res) {
                fetchProducts();
                setError(null);
                setShowAddForm(false);
            } else {
                setError(res.message);
            }
            setIsLoading(false);
        } catch (error) {
            setError(error.response.data.message);
            setIsLoading(false);
        }
    }

    const handleCreateCategory = async (e) => {
        e.preventDefault();
        try {
            setIsLoading(true);
            const res = await CategoriesQueriesUser.create({name: e.target.name.value});
        } catch (error) {
            setError(error.response.data.message);
            setIsLoading(false);
        }
      }

    const fetchCategories = async () => {
        try {
            setIsLoading(true);
            const res = await CategoriesQueriesUser.findAll();
            if(res) {
                setCategories(res.data.categories);
            }
            setIsLoading(false);
        }
        catch (error) {
            setError(error.response.data.message);
            setIsLoading(false);
        }
    }

    const fetchProducts = async () => {
        try {
            setIsLoading(true);
            if(debouncedSearch) {
                const res = await ProductsQueriesUser.findBySearchWithDetails(debouncedSearch);
                if(res) {
                    setProducts(res.data.products);
                } else {
                    setError(res.data.message);
                }
            } else {
                const res = await ProductsQueriesUser.findAll();
                if(res) {
                    setProducts(res.data.products);
                } else {
                    setError(res.data.message);
                }
            }
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
        }
    }

    // Debounce effect pour la recherche
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
        }, 1000);

        return () => clearTimeout(timer);
    }, [search]);

    // Effect pour déclencher la recherche quand debouncedSearch change
    useEffect(() => {
        fetchProducts();
        fetchCategories();
    }, [debouncedSearch]);

    return (
      <div className="products-section">
        {error && 
          <div className="error-overlay">
              <div className="error-message">{error}</div>
          </div>
        }
        <div className="section-header">
          <h2>Garment collection</h2>
          <button 
            className="btn btn-primary"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            <i className="bi bi-plus-circle"></i>
            Add a garment
          </button>
        </div>
  
        {showAddForm && (
          <div className="add-product-form mb-5">
            <h3>New garment</h3>
            <form onSubmit={handleCreateProduct}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Garment name</label>
                  <input type="text" name='name' className="form-control" placeholder="Ex: T-shirt Street Style" />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-success">Add the garment</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddForm(false)}>
                  Cancel the form
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="section-header">
          <h2>Garment categories</h2>
          <button 
            className="btn btn-primary"
            onClick={() => setShowAddCategoryForm(!showAddCategoryForm)}
          >
            <i className="bi bi-plus-circle"></i>
            Manage categories
          </button>
        </div>

        {showAddCategoryForm && (
          <div className="add-product-form mb-5">
            <h3>New garment category</h3>
            <form onSubmit={handleCreateCategory}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Category name</label>
                  <input type="text" name='name' className="form-control" placeholder="Ex: T-shirt Street Style" />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-success">Add garment category</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddCategoryForm(false)}>
                  Cancel the form
                </button>
              </div>
            </form>
            <h3>
              Existing garment categories
            </h3>
            <div className="categories-list">
              <ul
                className="list-unstyled"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: "16px",
                }}
              >
                {categories.map((category) => (
                  <li
                    className="list-unstyled-item"
                    key={category.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 12px",
                      border: "1px solid #eee",
                      borderRadius: "8px",
                      background: "#fafafa",
                    }}
                  >
                    {category.name}
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={async () => {
                        await CategoriesQueriesUser.delete(category.id);
                        fetchCategories();
                      }}
                    >
                      Delete
                    </button>
                  </li>
                ))}
              </ul>        
            </div>
          </div>
        )}
  
        <div className="products-filters">
          <div className="filter-group">
            <label className="form-label">Filter by type:</label>
            <select onChange={async (e) =>{
              const search = e.target.value;
              if(search.length > 0) {
                const res = await ProductsQueriesUser.findByCategoriesFilter(search);
                if(res) {
                  setProducts(res.data.products);
                } else {
                  setError(res.data.message);
                }
              }else{
                fetchProducts();
              }
            }} className="form-control">
              <option value={''}>All types</option>
              {
                categories.map((category) => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))
              }
            </select>
          </div>
          <div className="filter-group">
            <label className="form-label">Search:</label>
            <input type="search" className="form-control" placeholder="Ex: T-shirt Street Style" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
  
        <div className="products-list">
          <h3>Current collection</h3>
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th style={{minWidth: '100px'}}>Price</th>
                  <th>Color</th>
                  <th>Size</th>
                  <th>Stock</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {
                    products.map((product) => {
                      // Trouver le prix par défaut ou le premier prix disponible
                      const defaultPrice = product.prices?.find((price) => price.id === product.default_price_id);
                      const firstPrice = product.prices?.[0];
                      const displayPrice = defaultPrice || firstPrice;
                      
                      return (
                        <tr key={product.id}>
                          <td>
                            <div className="product-image">
                              <img 
                                className='img-contain' 
                                height={50} 
                                width={50} 
                                src={product.images?.[0]?.image_url || 'https://placehold.co/200'} 
                                alt={product.name} 
                              />
                            </div>
                          </td>
                          <td>{product.name}</td>
                          <td>{product.categories?.map(category => category.name).join(', ') || 'No category'}</td>
                          <td>{displayPrice?.price || 'N/A'} €</td>
                          <td>{product.variants?.length ? [...new Set(product.variants.map(variant => variant.color))].join(', ') : 'No variant'}</td>
                          <td>{product.variants?.length ? [...new Set(product.variants.map(variant => variant.size))].join(', ') : 'No variant'}</td>
                          <td>{product.variants?.reduce((acc, variant) => acc + variant.stock, 0) || 0}</td>
                          <td>
                            <button className="btn btn-sm btn-outline-primary" onClick={() => handleSelectProduct(product)}>Edit</button>
                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteProduct(product.id)}>Delete</button>
                          </td>
                        </tr>
                      );
                    })
                }
              </tbody>
            </table>
          </div>
        </div>
        {selectedProduct && ReactDOM.createPortal(
          <ProductFormModal product={selectedProduct} handleAddCategoryToProduct={handleAddCategoryToProduct} handleRemoveCategoryFromProduct={handleRemoveCategoryFromProduct} categories={categories} handleCloseModal={handleCloseModal} />,
          document.body
        )}
      </div>
    );
  };

const ProductFormModal = ({ product, handleCloseModal, categories, handleAddCategoryToProduct, handleRemoveCategoryFromProduct }) => {

    const [currentProduct, setCurrentProduct] = useState(product);
    const [error, setError] = useState(null);
    const [name, setName] = useState(product.name || '');
    const [description, setDescription] = useState(Array.isArray(product.description) ? product.description[0] : product.description);
    const [isLoading, setIsLoading] = useState(false);
    const [newPrice, setNewPrice] = useState(0);
    const [color, setColor] = useState(null);
    const [size, setSize] = useState(null);
    const [stock, setStock] = useState(0);
    const [productIndex, setProductIndex] = useState(product.product_index ?? 0);

    const fetchProduct = async () => {
        setIsLoading(true);
        const res = await ProductsQueriesUser.findById(product.id);
        if(res) {
            setCurrentProduct(res.data.product);
        } else {
            setError(res.data.message);
        }
        setIsLoading(false);
    }

    const handleUpdate = async () => {
        const res = await ProductsQueriesUser.update(currentProduct.id, {
            description: JSON.stringify([description]),
            name: name,
            product_index: productIndex
        });
        if(res) {
            if(res.data.product.name) {
                setName(res.data.product.name);
            }
            try {
                const parsedDescription = JSON.parse(res.data.product.description);
                setDescription(Array.isArray(parsedDescription) ? parsedDescription[0] : parsedDescription);
            } catch (e) {
                setDescription(res.data.product.description);
            }
        } else {
            setError(res.data.message);
        }
        fetchProduct();
    }

    useEffect(() => {
        fetchProduct();
    }, [product]);

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
        margin: 0,
        padding: 0,
        border: 'none',
        outline: 'none'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          maxWidth: '1100px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          boxShadow: '0 12px 24px 0 rgba(0, 0, 0, 0.5)',
          border: '4px solid black',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3>Edit garment</h3>
            <button className="btn btn-sm btn-outline-primary" onClick={async () => {
              await ProductsQueriesUser.update(currentProduct.id, {is_active: !currentProduct.is_active});
              fetchProduct();
            }}>
              {currentProduct.is_active ? 'Disable' : 'Enable'}
            </button>
            <button className="btn btn-sm btn-outline-secondary" onClick={handleCloseModal}>Close</button>
          </div>
          <div className="form-row">
            {error && <div className='form-error'>{error}</div>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {
                currentProduct ? (
                <>
                    <div className="form-group">
                        <div className="form-row">
                          <div className="form-group">
                            <label className="form-label">Garment index</label>
                            <input type="number" name='product_index' className="form-control" value={productIndex} onChange={(e) => setProductIndex(e.target.value)} />
                          </div>
                        </div>
                        <label className="form-label">Garment name</label>
                        <input 
                        disabled={isLoading}
                        type="text" 
                        name='name' 
                        className="form-control" 
                        placeholder="Ex: T-shirt Street Style" 
                        defaultValue={name}
                        onChange={(e) => setName(e.target.value)}
                        />
                        <label className="form-label">Garment description</label>
                        {
                            currentProduct.description && (
                                <div className="form-group">
                                    <textarea
                                        disabled={isLoading}
                                        defaultValue={currentProduct.description}
                                        rows={10}
                                        name='description' 
                                        className="form-control" 
                                        placeholder="Ex: T-shirt Street Style" 
                                        onChange={(e) => setDescription(e.target.value)}
                                        value={description}
                                    />
                                </div>
                            )
                        }
                    </div>
                </>
                ) : (
                    <div className="text-center">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                )
            }
            {
              <div className="form-group mt-3" style={{display: 'flex', flexDirection: 'row', gap: '10px', alignItems: 'center'}}>
                <label className="form-label">Garment price</label>
                <select className="form-control" name='default_price' value={currentProduct?.default_price_id} onChange={async (e) => {
                  setIsLoading(true);
                  await ProductsQueriesUser.update(currentProduct.id, {default_price_id: e.target.value});
                  setIsLoading(false);
                  fetchProduct();
                }}>
                  <option value={null}>Select a price</option>
                  {
                    currentProduct?.prices?.map((price) => (
                      <option key={price.id} value={price.id}>{price.price} €</option>
                    ))
                  }
                </select>
                <input 
                  disabled={isLoading}
                  type="number"
                  name='price'
                  className="form-control"
                  placeholder="Ex: 100"
                  value={newPrice}
                  onChange={(e) => setNewPrice(e.target.value)}
                  min={5}
                />
                <button className="btn btn-primary" onClick={async () => {
                   await PricesQueriesClient.create({productId: currentProduct.id, price: newPrice});
                   fetchProduct();
                   setNewPrice(0);
                }}>Add price</button>
              </div>
            }
            {
              <div className="form-group mt-3">
                {
                  categories.map((category) => (
                    <div key={category.id} className="form-check">
                      <input 
                        className="form-check-input" 
                        disabled={isLoading} 
                        type="checkbox" 
                        checked={!!currentProduct?.categories?.find(c => c.id === category.id)} 
                        value={category.id} 
                        onChange={async (e) => {
                          if(e.target.checked) {
                            await handleAddCategoryToProduct(currentProduct.id, category.id);
                            fetchProduct();
                          } else {
                            await handleRemoveCategoryFromProduct(currentProduct.id, category.id);
                            fetchProduct();
                          }
                        }} 
                      />
                      <label className="form-check-label">{category.name}</label>
                    </div>
                  ))
                }
              </div>
            }
            {
              <div className="form-group mt-3">
                <label className="form-label">Garment variants</label>
                <div className="form-check">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Color</th>
                        <th>Size</th>
                        <th>Stock</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {
                        currentProduct?.variants?.map((variant) => (
                          <VariantEditor key={variant.id} variant={variant} fetchProduct={fetchProduct} />
                        ))
                      }
                      <tr>
                        <td>
                          <select className='form-control' value={color} onChange={(e) => setColor(e.target.value)}>
                            <option value={null}>Select a color</option>
                            <option value="noir">Black</option>
                            <option value="blanc">White</option>
                            <option value="gris">Grey</option>
                            <option value="rouge">Red</option>
                            <option value="vert">Green</option>
                            <option value="bleu">Blue</option>
                            <option value="jaune">Yellow</option>
                            <option value="orange">Orange</option>
                            <option value="rose">Rose</option>
                            <option value="violet">Violet</option>
                            <option value="marron">Brown</option>
                            <option value="beige">Beige</option>
                            <option value="couleur unique">Single Color</option>
                          </select>
                        </td>
                        <td>
                          <select className='form-control' value={size} onChange={(e) => setSize(e.target.value)}>
                            <option value={null}>Select a size</option>
                            <option value="XS">XS</option>
                            <option value="S">S</option>
                            <option value="M">M</option> 
                            <option value="L">L</option>
                            <option value="XL">XL</option>
                            <option value="XXL">XXL</option>
                            <option value={"One Size"}>One Size</option>
                          </select>
                        </td>
                        <td><input type="number" name='stock' className="form-control" value={stock} onChange={(e) => setStock(e.target.value)} /></td>
                        <td>
                          <button className="btn btn-primary" onClick={async () => {
                            await VariantsProductsQueriesUser.create({productId: currentProduct.id, color: color, size: size, stock: stock});
                            fetchProduct();
                          }}>Add variant</button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            }
            {
              <div className="form-group mt-3">
                <label className="form-label">Garment images</label>
                <input type="file" name='images' className="form-control" onChange={async (e) => {
                  const file = e.target.files[0];
                  const formData = new FormData();
                  formData.append('image', file);
                  const res = await ImagesQueries.create(currentProduct.id, file);
                  fetchProduct();
                }} />
                <Gallery images={currentProduct?.images?.map((image) => { return { src: image.image_url, alt: currentProduct.name } }) || []} />
                <div style={{display: 'flex', flexDirection: 'row', gap: '10px', alignItems: 'center'}} className='form-group mt-3'>
                  {
                    currentProduct?.images?.map((image) => (
                      <div key={image.id} style={{flex: 1}}>
                        <img style={{objectFit: 'contain'}} width={100} height={100} src={image.image_url} alt={currentProduct.name} />
                        <button className="btn btn-sm btn-outline-danger" onClick={async () => {await ImagesQueries.delete(image.id); fetchProduct();}}>Delete</button>
                      </div>
                    ))
                  }
                </div>
              </div>
            }
          </div>
          <div className="form-actions" style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
            <button type="button" className="btn btn-primary" onClick={async ()=>{await handleUpdate(); handleCloseModal();}}>Update</button>
            <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>Cancel</button>
            {error && <div className="text-danger">{error}</div>}
          </div>
        </div>
      </div>
    )
}

const VariantEditor = ({ variant, fetchProduct }) => {

  const [stock, setStock] = useState(variant.stock);
  const [color, setColor] = useState(variant.color);
  const [size, setSize] = useState(variant.size);

  const handleDeleteVariant = async () => {
    await VariantsProductsQueriesUser.delete(variant.id);
    fetchProduct();
  }

  return (
    <tr key={variant.id}>
      <td>
        <select className='form-control' value={color} onChange={(e) => setColor(e.target.value)}>
          <option value={null}>Select a color</option>
          <option value="noir">Black</option>
          <option value="blanc">White</option>
          <option value="gris">Grey</option>
          <option value="rouge">Red</option>
          <option value="vert">Green</option>
          <option value="bleu">Blue</option>
          <option value="jaune">Yellow</option>
          <option value="orange">Orange</option>
          <option value="rose">Rose</option>
          <option value="violet">Violet</option>
          <option value="marron">Brown</option>
          <option value="beige">Beige</option>
          <option value="couleur unique">Unique Color</option>
        </select>
      </td>
      <td>
        <select className='form-control' value={size} onChange={(e) => setSize(e.target.value)}>
          <option value={null}>Select a size</option>
          <option value="XS">XS</option>
          <option value="S">S</option>
          <option value="M">M</option> 
          <option value="L">L</option>
          <option value="XL">XL</option>
          <option value="XXL">XXL</option>
          <option value={"One Size"}>One Size</option>
        </select>
      </td>
      <td><input type="number" name='stock' className="form-control" value={stock} onChange={(e) => setStock(e.target.value)} /></td>
      <td>
        <button type='button' className="btn btn-sm btn-outline-primary" onClick={async () => {
          await VariantsProductsQueriesUser.update(variant.id, {stock: stock, color: color, size: size});
          fetchProduct();
        }}>Update</button>
        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteVariant(variant.id)}>Delete</button>
      </td>
    </tr>
  );
}

export default ProductsManagement;